import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, timer, Subscription } from "rxjs";
import { switchMap, tap, catchError } from "rxjs/operators";
import { of } from "rxjs";
import { RestApiService } from "./rest-api.service";

export interface NotificationItem {
	module: string;
	key: string;
	label: string;
	route: string;
	count: number;
}

export interface NotificationModuleGroup {
	module: string;
	total: number;
	items: NotificationItem[];
}

export interface NotificationPayload {
	total: number;
	modules: NotificationModuleGroup[];
}

const REFRESH_INTERVAL_MS = 60_000;

@Injectable({ providedIn: "root" })
export class NotificationService {
	private readonly empty: NotificationPayload = { total: 0, modules: [] };
	private subject = new BehaviorSubject<NotificationPayload>(this.empty);

	public counts$: Observable<NotificationPayload> = this.subject.asObservable();
	private polling?: Subscription;

	constructor(private rest: RestApiService) {}

	startAutoRefresh(): void {
		if (this.polling) return;
		this.polling = timer(0, REFRESH_INTERVAL_MS)
			.pipe(switchMap(() => this.fetch$()))
			.subscribe();
	}

	stopAutoRefresh(): void {
		this.polling?.unsubscribe();
		this.polling = undefined;
		this.subject.next(this.empty);
	}

	refresh(): void {
		this.fetch$().subscribe();
	}

	getModule(moduleName: string): NotificationModuleGroup | undefined {
		return this.subject.value.modules.find((m) => m.module === moduleName);
	}

	getModuleTotal(moduleName: string): number {
		return this.getModule(moduleName)?.total ?? 0;
	}

	getKeyCount(key: string): number {
		for (const m of this.subject.value.modules) {
			const item = m.items.find((it) => it.key === key);
			if (item) return item.count;
		}
		return 0;
	}

	private fetch$(): Observable<NotificationPayload> {
		return this.rest.getCustom("notifications/pending-notifications").pipe(
			tap((payload: NotificationPayload) => this.subject.next(payload || this.empty)),
			catchError(() => of(this.empty)),
		);
	}
}
